import * as React from "react";
import { SettingsService } from "../../services/SettingsService";
import { getContainer } from "../../ioc/IOCSetup";
import { LoadingViewState, RouterView } from "../RouterView";
import { Form, FormListener } from "../form/Form";
import { RouteComponentProps, withRouter, Link } from "react-router-dom";
import { Fragment } from "react";
import { FormSubmitError, FormSubmitSuccess } from "../form/FormFeedback";
import { FormGroup } from "../form/FormGroup";
import { FormInput } from "../form/FormInput";
import { Submit } from "../form/Submit";
import { Settings } from "../../http/protocol";
import { t } from "../../i18n";

interface Props extends RouteComponentProps {}

interface State extends LoadingViewState {
    settings?: Settings;
    validationMap?: any;
}

export class SettingsKycView extends RouterView<Props, State> implements FormListener<Settings> {
    settingsService: SettingsService;

    constructor(props: Props) {
        super(props);
        this.settingsService = getContainer().getSettingsService();
        this.state = { validationMap: {}, loading: true };
    }

    loadContent(): Promise<any> | undefined {
        return this.settingsService.getSettings().then((data) => {
            this.setState({ settings: data });
        });
    }

    formDidUpdate(formModel: Settings, validationMap: any) {
        this.setState({ settings: formModel, validationMap: validationMap });
    }

    onSubmit(): Promise<any> {
        if (!this.state.settings) {
            return Promise.reject("Unable to save settings");
        }
        return this.settingsService.saveSettings(this.state.settings).catch(() => {
            throw "Unable to create/save settings";
        });
    }

    onSubmitError() {}

    getTitle(): string {
        return t(`title.kycSettings`);
    }

    renderContent() {
        if (!this.state.settings) {
            return <span></span>;
        }
        const kycDisabled = !this.state.settings.matiEnabled;
        return (
            <Fragment>
                <Form className="form-horizontal" formListener={this} modelObject={this.state.settings} validationMap={this.state.validationMap}>
                    <div className={"card"}>
                        <div className="card-body">
                            <div className="row">
                                <div className="col-lg-12">
                                    <FormSubmitError />
                                    <FormSubmitSuccess text="Settings was updated successfully" />

                                    {kycDisabled ? (
                                        <div className="alert alert-warning mt-2">
                                            KYC is currently disabled because Metamap is not enabled. Enable Metamap to activate these settings.
                                        </div>
                                    ) : (
                                        <div className="alert alert-info">
                                            Metamap is enabled. Manage credentials and verification configuration in Integrations settings.
                                        </div>
                                    )}

                                    <h5>Deposit</h5>
                                    <FormGroup className="custom-control custom-checkbox" model="kycDepositNewUsers">
                                        <FormInput className="custom-control-input" model="kycDepositNewUsers" id={"kycDepositNewUsers"} type="checkbox" disabled={kycDisabled} />
                                        <label htmlFor={"kycDepositNewUsers"} className={"custom-control-label"}>Apply to newly created accounts only</label><br />
                                        <span className={"help-block"}>Only accounts created after enabling this setting must provide identity documents to deposit. Existing accounts are not affected.</span>
                                    </FormGroup>
                                    <FormGroup className="custom-control custom-checkbox" model="kycOnDepositRequired">
                                        <FormInput className="custom-control-input" model="kycOnDepositRequired" id={"kycOnDepositRequired"} type="checkbox" disabled={kycDisabled} />
                                        <label htmlFor={"kycOnDepositRequired"} className={"custom-control-label"}>Apply to all users</label><br />
                                        <span className={"help-block"}>All users need to provide identity documents in order to deposit funds. If enabled, it overrides per-user and “new users” settings.</span>
                                    </FormGroup>

                                    <h5 className="mt-3">Withdraw</h5>
                                    <FormGroup className="custom-control custom-checkbox" model="kycWithdrawNewUsers">
                                        <FormInput className="custom-control-input" model="kycWithdrawNewUsers" id={"kycWithdrawNewUsers"} type="checkbox" disabled={kycDisabled} />
                                        <label htmlFor={"kycWithdrawNewUsers"} className={"custom-control-label"}>Apply to newly created accounts only</label><br />
                                        <span className={"help-block"}>Only accounts created after enabling this setting must provide identity documents to withdraw funds. Existing accounts are not affected.</span>
                                    </FormGroup>
                                    <FormGroup className="custom-control custom-checkbox" model="kycOnWithdrawRequired">
                                        <FormInput className="custom-control-input" model="kycOnWithdrawRequired" id={"kycOnWithdrawRequired"} type="checkbox" disabled={kycDisabled} />
                                        <label htmlFor={"kycOnWithdrawRequired"} className={"custom-control-label"}>Apply to all users</label><br />
                                        <span className={"help-block"}>All users need to provide identity documents in order to withdraw funds. If enabled, it overrides per-user and “new users” settings.</span>
                                    </FormGroup>

                                    <h5 className="mt-3">Play</h5>
                                    <FormGroup className="custom-control custom-checkbox" model="kycPlayNewUsers">
                                        <FormInput className="custom-control-input" model="kycPlayNewUsers" id={"kycPlayNewUsers"} type="checkbox" disabled={kycDisabled} />
                                        <label htmlFor={"kycPlayNewUsers"} className={"custom-control-label"}>Apply to newly created accounts only</label><br />
                                        <span className={"help-block"}>Only accounts created after enabling this setting must provide identity documents to play. Existing accounts are not affected.</span>
                                    </FormGroup>
                                    <FormGroup className="custom-control custom-checkbox" model="kycOnPlayRequired">
                                        <FormInput className="custom-control-input" model="kycOnPlayRequired" id={"kycOnPlayRequired"} type="checkbox" disabled={kycDisabled} />
                                        <label htmlFor={"kycOnPlayRequired"} className={"custom-control-label"}>Apply to all users</label><br />
                                        <span className={"help-block"}>All users need to provide identity documents in order to play any games. If enabled, it overrides per-user and “new users” settings.</span>
                                    </FormGroup>

                                    <h5 className="mt-3">KYC Available</h5>
                                    <FormGroup className="custom-control custom-checkbox" model="kycAvailableNewUsers">
                                        <FormInput className="custom-control-input" model="kycAvailableNewUsers" id={"kycAvailableNewUsers"} type="checkbox" disabled={kycDisabled} />
                                        <label htmlFor={"kycAvailableNewUsers"} className={"custom-control-label"}>Apply to newly created accounts only</label><br />
                                        <span className={"help-block"}>Only accounts created after enabling this setting will be able to verify their account from their profile page. Existing accounts are not affected.</span>
                                    </FormGroup>
                                    <FormGroup className="custom-control custom-checkbox" model="kycAvailable">
                                        <FormInput className="custom-control-input" model="kycAvailable" id={"kycAvailable"} type="checkbox" disabled={kycDisabled} />
                                        <label htmlFor={"kycAvailable"} className={"custom-control-label"}>Apply to all users</label><br />
                                        <span className={"help-block"}>All users will be able to verify their account from their profile page if not already verified.</span>
                                    </FormGroup>

                                    <h5 className="mt-3">Mandatory KYC</h5>
                                    <span className={"help-block"}>Requires KYC verification at registration and login. Requires configuration per User Journey.</span><br />
                                    <Link to={"/user-journeys"}>Manage User Journeys</Link>

                                    <h5 className="mt-4">All sensitive actions require KYC – Overrides the above</h5>
                                    <FormGroup className="custom-control custom-checkbox" model="kycAllNewUsers">
                                        <FormInput className="custom-control-input" model="kycAllNewUsers" id={"kycAllNewUsers"} type="checkbox" disabled={kycDisabled} />
                                        <label htmlFor={"kycAllNewUsers"} className={"custom-control-label"}>Apply to newly created accounts only</label><br />
                                        <span className={"help-block"}>Only accounts created after enabling this setting must provide identity documents for all sensitive actions. Existing accounts are not affected.</span>
                                    </FormGroup>
                                    <FormGroup className="custom-control custom-checkbox" model="kycOnAllRequired">
                                        <FormInput className="custom-control-input" model="kycOnAllRequired" id={"kycOnAllRequired"} type="checkbox" disabled={kycDisabled} />
                                        <label htmlFor={"kycOnAllRequired"} className={"custom-control-label"}>Apply to all users</label><br />
                                        <span className={"help-block"}>All users need to provide identity documents for all sensitive actions. If enabled, it overrides per-user and section-level KYC settings above.</span>
                                    </FormGroup>

                                    <span className={"help-block"}>
                                        You can configure KYC requirement for specific withdraw/deposit methods in <Link to={"/admin/settings/advanced"}>Advanced Config</Link>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={"card"}>
                        <div className={"card-body"}>
                            <div className="row">
                                <div className="col-lg-12">
                                    <div className="ibox float-e-margins">
                                        <div className="ibox-content">
                                            <div className="ibox-title">
                                                <h5>Actions</h5>
                                            </div>
                                            <div className="form-group">
                                                <div className="col-sm-10 col-sm-offset-2">
                                                    <Submit className="btn btn-primary waves-effect waves-light">Save Settings</Submit>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Form>
            </Fragment>
        );
    }
}

export default withRouter(SettingsKycView);


